/**
 * Built-in middleware for the event bus
 */

import type { AgentEvent, EventMiddleware } from './types.js';
import { useEventLogStore } from '@/stores/eventLogStore.js';
import { useNotificationStore } from '@/stores/notificationStore.js';
import { useSessionStore } from '@/stores/sessionStore.js';

/**
 * Logging middleware - logs all events to console
 */
export const loggingMiddleware: EventMiddleware = (event: AgentEvent) => {
  // Temporarily disabled to reduce noise during debugging
  // if (process.env.NODE_ENV === 'development') {
  //   console.log(`[Event] ${event.type}`, {
  //     ...event,
  //     timestamp: new Date(event.timestamp).toISOString(),
  //   });
  // }
  return event;
};

/**
 * Activity feed middleware - adds events to activity log
 */
export const activityMiddleware: EventMiddleware = (event: AgentEvent) => {
  const eventLog = useEventLogStore.getState();

  // Map event to activity log entry
  const activityMap: Record<string, { category: string; title: string; type: string }> = {
    thinking: {
      category: 'agent',
      title: 'Agent thinking',
      type: 'info',
    },
    response: {
      category: 'agent',
      title: 'Agent responded',
      type: 'success',
    },
    error: {
      category: 'agent',
      title: 'Agent error',
      type: 'error',
    },
    toolCall: {
      category: 'tool',
      title: 'Tool execution',
      type: 'info',
    },
    approvalRequest: {
      category: 'system',
      title: 'Approval requested',
      type: 'warning',
    },
    taskCreated: {
      category: 'system',
      title: 'Task created',
      type: 'info',
    },
    taskUpdated: {
      category: 'system',
      title: 'Task updated',
      type: 'info',
    },
    connectionStatus: {
      category: 'system',
      title: 'Connection status',
      type: 'info',
    },
    'scheduler:scheduleCreated': {
      category: 'scheduler',
      title: 'Schedule created',
      type: 'success',
    },
    'scheduler:scheduleUpdated': {
      category: 'scheduler',
      title: 'Schedule updated',
      type: 'info',
    },
    'scheduler:scheduleDeleted': {
      category: 'scheduler',
      title: 'Schedule deleted',
      type: 'info',
    },
    'scheduler:scheduleTriggered': {
      category: 'scheduler',
      title: 'Schedule running',
      type: 'info',
    },
    'scheduler:executionCompleted': {
      category: 'scheduler',
      title: 'Schedule completed',
      type: 'success',
    },
    'scheduler:executionFailed': {
      category: 'scheduler',
      title: 'Schedule failed',
      type: 'error',
    },
  };

  const mapping = activityMap[event.type];
  if (mapping) {
    let description: string | undefined;
    let metadata: Record<string, unknown> | undefined;

    // Type-specific data extraction
    switch (event.type) {
      case 'response':
        description = event.text.slice(0, 100);
        metadata = { tokenUsage: event.tokenUsage };
        break;
      case 'error':
        description = event.error;
        break;
      case 'toolCall':
        description = `Calling ${event.toolName}`;
        metadata = { tool: event.toolName, args: event.toolArgs };
        break;
      case 'approvalRequest':
        if (event.approvalType === 'elicitation' && 'metadata' in event) {
          const prompt = event.metadata.prompt as string;
          description = prompt || 'Input requested';
        } else if (event.approvalType === 'tool_confirmation' && 'toolName' in event) {
          description = event.description || `Approve ${event.toolName}`;
        } else if (event.approvalType === 'command_confirmation' && 'command' in event) {
          description = `Approve command: ${event.command}`;
        }
        metadata = { approvalId: event.approvalId, approvalType: event.approvalType };
        break;
      case 'taskCreated':
        description = event.task.request?.text;
        metadata = { taskId: event.task.taskId, direction: event.task.direction };
        break;
      case 'taskUpdated':
        description = `Status: ${event.task.status}`;
        metadata = { taskId: event.task.taskId, status: event.task.status };
        break;
      case 'connectionStatus':
        description = `Connection ${event.status}`;
        break;
      case 'scheduler:scheduleCreated':
        description = event.schedule.name;
        metadata = { scheduleId: event.schedule.id, cron: event.schedule.cronExpression };
        break;
      case 'scheduler:scheduleUpdated':
        description = event.schedule.name;
        metadata = { scheduleId: event.schedule.id };
        break;
      case 'scheduler:scheduleDeleted':
        description = event.scheduleName;
        metadata = { scheduleId: event.scheduleId };
        break;
      case 'scheduler:scheduleTriggered':
        description = `Running: ${event.schedule.name}`;
        metadata = { scheduleId: event.schedule.id };
        break;
      case 'scheduler:executionCompleted':
        description = `${event.schedule.name} completed`;
        metadata = { scheduleId: event.schedule.id, duration: event.log.duration };
        break;
      case 'scheduler:executionFailed':
        description = `${event.schedule.name}: ${event.error}`;
        metadata = { scheduleId: event.schedule.id, error: event.error };
        break;
    }

    eventLog.addEvent({
      type: mapping.type as 'info' | 'success' | 'warning' | 'error',
      category: mapping.category as 'user' | 'system' | 'agent' | 'tool',
      title: mapping.title,
      description,
      sessionId: 'sessionId' in event ? event.sessionId : undefined,
      metadata,
    });
  }

  return event;
};

// Track which sessions have been notified about to avoid duplicate notifications
const notifiedSessions = new Set<string>();

// Track if we're currently replaying history (to suppress notifications)
let isReplayingHistory = false;

/**
 * Set replay state (used by SessionService during history replay)
 */
export function setHistoryReplayState(replaying: boolean): void {
  isReplayingHistory = replaying;
}

/**
 * Cleanup session-specific notification state (called when a session is deleted)
 */
export function cleanupSessionNotification(sessionId: string): void {
  notifiedSessions.delete(sessionId);
}

/**
 * Notification middleware - creates user-facing notifications for important events
 */
export const notificationMiddleware: EventMiddleware = (event: AgentEvent) => {
  // Skip notifications during history replay
  if (isReplayingHistory) {
    return event;
  }

  const notificationStore = useNotificationStore.getState();
  const currentSessionId = useSessionStore.getState().currentSessionId;

  // Determine if this event should create a notification
  const shouldNotify = (e: AgentEvent): boolean => {
    const eventSessionId = 'sessionId' in e ? e.sessionId : undefined;

    // Don't notify for events in current session (user is watching)
    if (eventSessionId && eventSessionId === currentSessionId) {
      return false;
    }

    // Notify about new sessions (first thinking event for a new session)
    if (
      e.type === 'thinking' &&
      eventSessionId &&
      !notifiedSessions.has(eventSessionId)
    ) {
      notifiedSessions.add(eventSessionId);
      return true;
    }

    // Always notify for certain event types
    if (e.type === 'approvalRequest') return true;
    if (e.type === 'error') return true;

    // Notify for task events
    if (e.type === 'taskCreated' && 'task' in e && e.task.direction === 'incoming') {
      return true;
    }

    if (
      e.type === 'taskUpdated' &&
      'task' in e &&
      ['completed', 'failed'].includes(e.task.status)
    ) {
      return true;
    }

    // Notify when background session completes response
    if (e.type === 'response' && eventSessionId && eventSessionId !== currentSessionId) {
      return true;
    }

    // Notify for scheduler events
    if (e.type === 'scheduler:scheduleCreated') return true;
    if (e.type === 'scheduler:scheduleTriggered') return true;
    if (e.type === 'scheduler:executionCompleted') return true;
    if (e.type === 'scheduler:executionFailed') return true;

    return false;
  };

  if (shouldNotify(event)) {
    let title = '';
    let description: string | undefined;
    let intent: 'info' | 'warning' | 'success' | 'danger' = 'info';
    const eventSessionId = 'sessionId' in event ? event.sessionId : undefined;

    switch (event.type) {
      case 'thinking':
        title = 'New session started';
        description = 'A new conversation has been initiated';
        intent = 'info';
        break;

      case 'response':
        title = 'New message received';
        description = event.text.slice(0, 100) + (event.text.length > 100 ? '...' : '');
        intent = 'success';
        break;

      case 'approvalRequest':
        if (event.approvalType === 'elicitation') {
          title = 'Input needed';
          description = (event.metadata.prompt as string) || 'Agent needs additional information';
          intent = 'info';
        } else if (event.approvalType === 'command_confirmation' && 'command' in event) {
          title = 'Command approval needed';
          description = `Approve command: ${event.command.slice(0, 100)}`;
          intent = 'warning';
        } else {
          title = 'Approval needed';
          description = ('description' in event && event.description) || ('toolName' in event && `Approve ${event.toolName}`) || 'Approval required';
          intent = 'warning';
        }
        break;

      case 'error':
        title = 'Error occurred';
        description = event.error;
        intent = 'danger';
        break;

      case 'taskCreated':
        title = 'New delegation received';
        description = event.task.request?.text;
        intent = 'info';
        break;

      case 'taskUpdated':
        if (event.task.status === 'completed') {
          title = 'Task completed';
          description = event.task.response?.text;
          intent = 'success';
        } else if (event.task.status === 'failed') {
          title = 'Task failed';
          description = event.task.error;
          intent = 'danger';
        }
        break;

      case 'scheduler:scheduleCreated':
        title = 'Schedule created';
        description = `"${event.schedule.name}" has been scheduled`;
        intent = 'success';
        break;

      case 'scheduler:scheduleTriggered':
        title = 'Schedule running';
        description = `"${event.schedule.name}" has started`;
        intent = 'info';
        break;

      case 'scheduler:executionCompleted':
        title = 'Scheduled task completed';
        description = `"${event.schedule.name}" finished successfully`;
        intent = 'success';
        break;

      case 'scheduler:executionFailed':
        title = 'Scheduled task failed';
        description = `"${event.schedule.name}": ${event.error}`;
        intent = 'danger';
        break;
    }

    if (title) {
      notificationStore.addToast({
        title,
        description,
        intent,
        targetSessionId: eventSessionId,
      });
    }
  }

  return event;
};

/**
 * Analytics middleware - track events for analytics
 */
export const analyticsMiddleware: EventMiddleware = (event: AgentEvent) => {
  // TODO: Send to analytics service (Mixpanel, Amplitude, etc.)
  // For now, just pass through
  return event;
};

/**
 * Default middleware stack
 */
export const defaultMiddleware = [
  loggingMiddleware,
  activityMiddleware,
  notificationMiddleware,
  analyticsMiddleware,
];
